import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  CheckCircle2,
  Package,
  XCircle,
  Clock,
  MapPin,
  TrendingUp,
  Home,
  RotateCcw,
  Star
} from 'lucide-react';
import { useDriver } from '../context/DriverContext';
import { parcelEvents } from '@/shared/services/trailService';

const RouteComplete = () => {
  const navigate = useNavigate();
  const { activeRoute, deliveryHistory, setActiveRoute } = useDriver();

  const stops = activeRoute?.stops || [];
  const delivered = stops.filter(s => s.status === 'DELIVERED').length;
  const failed = stops.filter(s => s.status === 'FAILED').length;
  const totalPackages = stops.reduce((sum, s) => sum + (s.packages?.length || 0), 0);
  const deliveredPackages = stops
    .filter(s => s.status === 'DELIVERED')
    .reduce((sum, s) => sum + (s.packages?.length || 0), 0);

  const successRate = stops.length > 0 ? Math.round((delivered / stops.length) * 100) : 0;

  // Emit route completed event when component mounts
  useEffect(() => {
    if (activeRoute?.id) {
      const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
      parcelEvents.routeCompleted(activeRoute.id, {
        total_stops: stops.length,
        delivered,
        failed,
        total_packages: totalPackages,
        delivered_packages: deliveredPackages,
        success_rate: successRate
      }, driverId);
    }
  }, []); // Run once on mount

  const handleStartNewRoute = () => {
    setActiveRoute(null);
    navigate('/driver/route-load');
  };

  const handleGoHome = () => {
    setActiveRoute(null);
    navigate('/driver');
  };

  return (
    <div className="p-4 space-y-6" data-testid="route-complete">
      {/* Success Banner */}
      <div className="text-center py-6">
        <div className="mx-auto w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-10 w-10 text-green-500" />
        </div>
        <h1 className="text-2xl font-bold">Route Complete!</h1>
        <p className="text-muted-foreground mt-1">{activeRoute?.id}</p>
        
        {successRate >= 90 && (
          <div className="mt-4 inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">Excellent Performance!</span>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="h-6 w-6 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold text-green-700">{delivered}</p>
            <p className="text-sm text-green-600">Delivered</p>
          </CardContent>
        </Card>
        
        <Card className={failed > 0 ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}>
          <CardContent className="p-4 text-center">
            <XCircle className={`h-6 w-6 mx-auto mb-2 ${failed > 0 ? 'text-red-500' : 'text-gray-400'}`} />
            <p className={`text-2xl font-bold ${failed > 0 ? 'text-red-700' : 'text-gray-600'}`}>{failed}</p>
            <p className={`text-sm ${failed > 0 ? 'text-red-600' : 'text-gray-500'}`}>Failed</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Stats */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex justify-between items-center py-2 border-b">
            <span className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              Total Stops
            </span>
            <span className="font-semibold">{stops.length}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Package className="h-4 w-4" />
              Packages Delivered
            </span>
            <span className="font-semibold">{deliveredPackages} / {totalPackages}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Success Rate
            </span>
            <Badge variant={successRate >= 90 ? 'default' : successRate >= 70 ? 'secondary' : 'destructive'}>
              {successRate}%
            </Badge>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              Duration
            </span>
            <span className="font-semibold">
              {activeRoute?.startedAt 
                ? `${Math.round((new Date() - new Date(activeRoute.startedAt)) / 60000)} min`
                : '--'
              }
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Stop Summary */}
      <Card>
        <CardContent className="p-0 max-h-48 overflow-y-auto">
          {stops.map((stop, index) => (
            <div key={stop.id} className="px-4 py-3 border-b last:border-0 flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                stop.status === 'DELIVERED' ? 'bg-green-500' : 'bg-red-500'
              }`}>
                {stop.status === 'DELIVERED' ? (
                  <CheckCircle2 className="h-4 w-4 text-white" />
                ) : (
                  <XCircle className="h-4 w-4 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Stop {index + 1}</p>
                <p className="text-xs text-muted-foreground truncate">{stop.customer}</p>
              </div>
              <Badge variant={stop.status === 'DELIVERED' ? 'outline' : 'destructive'} className="text-xs">
                {stop.packages?.length || 0} pkg
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="space-y-3">
        <Button 
          onClick={handleStartNewRoute}
          className="w-full bg-black hover:bg-gray-800"
          size="lg"
        >
          <RotateCcw className="h-5 w-5 mr-2" />
          Start New Route
        </Button>
        
        <Button 
          onClick={handleGoHome}
          variant="outline"
          className="w-full"
          size="lg"
        >
          <Home className="h-5 w-5 mr-2" />
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
};

export default RouteComplete;
